import { getAccessToken } from '@/lib/tokenStorage';
import type { MediaSessionResponse, MediaSessionSnapshot } from '@/types/media';

const RENEW_MARGIN_MS = 15_000;
const MAX_SESSION_MS = 300_000;
const COOKIE_MUTATION_LOCK = 'ontoh-cctv-media-cookie-mutation';
type Identity = { origin: string; token: string };
type Binding = Identity & { expiresAt: number };
type Flight = Identity & { generation: number; promise: Promise<void> };

export class MediaSessionError extends Error {
  constructor(readonly status: number) {
    super(status === 501 ? '미디어를 재생하려면 최신 브라우저를 사용해 주세요.'
      : status === 409 ? '미디어 세션이 변경되었습니다.' : '미디어 세션을 확인하지 못했습니다.');
    this.name = 'MediaSessionError';
  }
}

let generation = 0;
let desired: Identity | null = null;
let binding: Binding | null = null;
let flight: Flight | null = null;
let mutationTail: Promise<void> = Promise.resolve();
let expiryTimer: ReturnType<typeof setTimeout> | null = null;
let storageListenerAttached = false;
let snapshot: MediaSessionSnapshot = { status: 'idle', expiresAt: null, version: 0 };
const listeners = new Set<() => void>();

function publish(status: MediaSessionSnapshot['status'], expiresAt: number | null): void {
  if (snapshot.status === status && snapshot.expiresAt === expiresAt) return;
  snapshot = { status, expiresAt, version: snapshot.version + 1 };
  listeners.forEach((listener) => listener());
}

function clearExpiryTimer(): void {
  if (expiryTimer !== null) clearTimeout(expiryTimer);
  expiryTimer = null;
}

function configuredOrigin(apiUrl: string): string {
  try {
    const api = new URL(apiUrl);
    if (!['http:', 'https:'].includes(api.protocol) || api.username || api.password) throw new Error();
    return api.origin;
  } catch {
    throw new MediaSessionError(400);
  }
}

function currentToken(): string | null {
  try { return getAccessToken(); } catch { return null; }
}

function sameIdentity(left: Identity | null, right: Identity): boolean {
  return left?.origin === right.origin && left.token === right.token;
}

function isCurrent(identity: Identity, requestGeneration: number): boolean {
  return generation === requestGeneration && sameIdentity(desired, identity) && currentToken() === identity.token;
}

function serializeMutation(operation: () => Promise<void>): Promise<void> {
  // Set-Cookie can arrive after a caller discards a request, so cookie writes must settle in order.
  const run = () => {
    if (!supportsCookieMutationLocks()) throw markUnsupported();
    return navigator.locks.request(COOKIE_MUTATION_LOCK, { mode: 'exclusive' }, operation);
  };
  const result = mutationTail.then(run, run);
  mutationTail = result.then(() => undefined, () => undefined);
  return result;
}

function supportsCookieMutationLocks(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function';
}

function markUnsupported(): MediaSessionError {
  invalidateMediaSession();
  publish('unsupported', null);
  return new MediaSessionError(501);
}

function onStorageChange(): void {
  const token = currentToken();
  if ((desired !== null && desired.token !== token) || (binding !== null && binding.token !== token)) {
    invalidateMediaSession();
  }
}

function listenForStorageChanges(): void {
  if (storageListenerAttached || typeof window === 'undefined') return;
  window.addEventListener('storage', onStorageChange);
  storageListenerAttached = true;
}

function scheduleExpiry(active: Binding, requestGeneration: number): void {
  clearExpiryTimer();
  expiryTimer = setTimeout(() => {
    if (binding !== active || generation !== requestGeneration) return;
    binding = null;
    expiryTimer = null;
    publish(flight ? 'binding' : 'idle', null);
  }, Math.max(0, active.expiresAt - Date.now()));
}

export function subscribeMediaSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function getMediaSessionSnapshot(): MediaSessionSnapshot {
  return snapshot;
}

export function isMediaSessionReady(): boolean {
  return snapshot.status === 'ready' && binding !== null && binding.expiresAt > Date.now()
    && sameIdentity(desired, binding) && currentToken() === binding.token;
}

export function invalidateMediaSession(): void {
  generation += 1;
  desired = null;
  binding = null;
  flight = null;
  clearExpiryTimer();
  if (storageListenerAttached) {
    window.removeEventListener('storage', onStorageChange);
    storageListenerAttached = false;
  }
  publish('idle', null);
}

export function ensureMediaSession(apiUrl: string, accessToken: string): Promise<void> {
  let origin: string;
  try { origin = configuredOrigin(apiUrl); } catch (error) { return Promise.reject(error); }
  if (!supportsCookieMutationLocks()) return Promise.reject(markUnsupported());
  const identity = { origin, token: accessToken };
  if (!accessToken || currentToken() !== accessToken) return Promise.reject(new MediaSessionError(409));
  if (flight && sameIdentity(flight, identity) && flight.generation === generation) return flight.promise;
  if (binding && sameIdentity(binding, identity) && isMediaSessionReady()
    && binding.expiresAt - Date.now() > RENEW_MARGIN_MS) return Promise.resolve();

  const keepValidBinding = binding !== null && sameIdentity(binding, identity) && isMediaSessionReady();
  if (!sameIdentity(desired, identity)) {
    generation += 1;
    binding = null;
    clearExpiryTimer();
  }
  desired = identity;
  listenForStorageChanges();
  const requestGeneration = generation;
  if (!keepValidBinding) publish('binding', null);
  const request: Flight = { ...identity, generation: requestGeneration, promise: Promise.resolve() };
  request.promise = serializeMutation(async () => {
    if (!isCurrent(identity, requestGeneration)) throw new MediaSessionError(409);
    try {
      const response = await fetch(`${origin}/api/v1/media/session`, {
        method: 'POST', credentials: 'include', cache: 'no-store',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!isCurrent(identity, requestGeneration)) throw new MediaSessionError(409);
      if (!response.ok) throw new MediaSessionError(response.status);
      const data: Partial<MediaSessionResponse> | null = await response.json();
      if (!isCurrent(identity, requestGeneration)) throw new MediaSessionError(409);
      const serverExpiry = typeof data?.expires_at === 'string' ? Date.parse(data.expires_at) : NaN;
      if (!Number.isFinite(serverExpiry) || serverExpiry <= Date.now()) throw new MediaSessionError(502);
      const active = { ...identity, expiresAt: Math.min(serverExpiry, Date.now() + MAX_SESSION_MS) };
      binding = active;
      scheduleExpiry(active, requestGeneration);
      publish('ready', active.expiresAt);
    } catch (error) {
      if (!isCurrent(identity, requestGeneration)) throw new MediaSessionError(409);
      binding = null;
      clearExpiryTimer();
      publish('error', null);
      throw error instanceof MediaSessionError ? error : new MediaSessionError(503);
    } finally {
      if (flight === request) flight = null;
    }
  });
  flight = request;
  return request.promise;
}

export function revokeMediaSession(apiUrl: string): Promise<void> {
  invalidateMediaSession();
  const revokedToken = currentToken();
  let origin: string;
  try { origin = configuredOrigin(apiUrl); } catch (error) { return Promise.reject(error); }
  if (!supportsCookieMutationLocks()) return Promise.reject(markUnsupported());
  return serializeMutation(async () => {
    const token = currentToken();
    const localReplacementQueued = flight !== null && flight.token === token
      && flight.generation === generation && sameIdentity(desired, flight);
    if (token !== null && token !== revokedToken && !localReplacementQueued) throw new MediaSessionError(409);
    try {
      const response = await fetch(`${origin}/api/v1/media/session`, {
        method: 'DELETE', credentials: 'include', cache: 'no-store',
      });
      if (!response.ok) throw new MediaSessionError(response.status);
    } catch (error) {
      throw error instanceof MediaSessionError ? error : new MediaSessionError(503);
    }
  });
}
